/**
 * Module Registry
 * Centralized configuration for all integrated sidebar modules from
 * EdgeStream DevTools and EdgeInteract Explorer.
 */

import React from 'react';
import {
  ServerIcon,
  GroupIcon,
  ChatIcon,
  TrendingUpIcon,
  ActivityIcon,
  PipelineIcon,
  HooksIcon,
  BellIcon,
  PlayIcon,
  HelpCircleIcon,
  PencilIcon,
  LogsIcon,
  SettingsIcon,
} from '@bizfirst/common-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type SourceModule = 'edge-stream' | 'edge-interact';

export interface ModuleItem {
  id: string;
  label: string;
  description: string;
  group: string;
  groupId: string;
  sourceModule: SourceModule;
  accentColor: string;
  route: string;
  icon: React.ReactNode;
}

export interface ModuleGroup {
  id: string;
  label: string;
  sourceModule: SourceModule;
  color: string;
  items: ModuleItem[];
}

// ─── EdgeStream modules (13 items) ────────────────────────────────────────────

export const EDGE_STREAM_MODULES: ModuleItem[] = [
  // Servers
  { id: 'es-servers',          label: 'Servers',               group: 'Servers',             groupId: 'servers',       sourceModule: 'edge-stream', accentColor: '#3A8C45', route: '/servers',               icon: <ServerIcon size={18} />,      description: 'Monitor and manage EdgeStream server connections' },
  { id: 'es-my-groups',        label: 'My Groups',             group: 'Servers',             groupId: 'servers',       sourceModule: 'edge-stream', accentColor: '#3A8C45', route: '/my-groups',             icon: <GroupIcon size={18} />,       description: 'View and manage your user groups and memberships' },
  // Communication
  { id: 'es-chat',             label: 'Chat',                  group: 'Communication',       groupId: 'communication', sourceModule: 'edge-stream', accentColor: '#10b981', route: '/chat',                  icon: <ChatIcon size={18} />,        description: 'Real-time chat and messaging interface' },
  // Diagnostics
  { id: 'es-events',           label: 'Event Stream',          group: 'Diagnostics',         groupId: 'diagnostics',   sourceModule: 'edge-stream', accentColor: '#f59e0b', route: '/events',                icon: <TrendingUpIcon size={18} />,  description: 'View and filter real-time event streams' },
  { id: 'es-activity',         label: 'Activity Stream',       group: 'Diagnostics',         groupId: 'diagnostics',   sourceModule: 'edge-stream', accentColor: '#f59e0b', route: '/activity-stream',       icon: <ActivityIcon size={18} />,    description: 'Monitor activity logs and stream observability' },
  // Pipeline Management
  { id: 'es-pipelines',        label: 'Pipelines',             group: 'Pipeline Management', groupId: 'pipelines',     sourceModule: 'edge-stream', accentColor: '#8b5cf6', route: '/pipelines',             icon: <PipelineIcon size={18} />,    description: 'Design and manage event processing pipelines' },
  { id: 'es-hooks-ref',        label: 'Available Hooks',       group: 'Pipeline Management', groupId: 'pipelines',     sourceModule: 'edge-stream', accentColor: '#8b5cf6', route: '/hooks-reference',       icon: <HooksIcon size={18} />,       description: 'Browse available hook definitions and specifications' },
  { id: 'es-subscribers-ref',  label: 'Available Subscribers', group: 'Pipeline Management', groupId: 'pipelines',     sourceModule: 'edge-stream', accentColor: '#8b5cf6', route: '/subscribers-reference', icon: <BellIcon size={18} />,        description: 'Browse available subscriber definitions and configurations' },
  { id: 'es-hooks',            label: 'Hook Execution',        group: 'Pipeline Management', groupId: 'pipelines',     sourceModule: 'edge-stream', accentColor: '#8b5cf6', route: '/hooks',                 icon: <HooksIcon size={18} />,       description: 'Monitor and debug hook execution history and results' },
  { id: 'es-subscribers',      label: 'Subscriber Delivery',   group: 'Pipeline Management', groupId: 'pipelines',     sourceModule: 'edge-stream', accentColor: '#8b5cf6', route: '/subscribers',           icon: <BellIcon size={18} />,        description: 'Track subscriber delivery status and delivery history' },
  // Demo & Learning
  { id: 'es-demo',             label: 'Demo',                  group: 'Demo & Learning',     groupId: 'demo',          sourceModule: 'edge-stream', accentColor: '#ec4899', route: '/demo',                  icon: <PlayIcon size={18} />,        description: 'Interactive demo scenarios and learning exercises' },
  // Help & Docs
  { id: 'es-help-hooks',       label: 'Hooks Library',         group: 'Help & Docs',         groupId: 'help',          sourceModule: 'edge-stream', accentColor: '#64748b', route: '/help/hooks',            icon: <HelpCircleIcon size={18} />,  description: 'Reference documentation for the hooks library' },
  { id: 'es-help-subscribers', label: 'Subscribers Help',      group: 'Help & Docs',         groupId: 'help',          sourceModule: 'edge-stream', accentColor: '#64748b', route: '/help/subscribers',      icon: <HelpCircleIcon size={18} />,  description: 'Reference documentation for subscribers' },
  { id: 'es-help-pipelines',   label: 'Pipelines Help',        group: 'Help & Docs',         groupId: 'help',          sourceModule: 'edge-stream', accentColor: '#64748b', route: '/help/pipelines',        icon: <HelpCircleIcon size={18} />,  description: 'Reference documentation for pipelines' },
];

// ─── EdgeInteract Explorer modules (5 items) ──────────────────────────────────

export const EXPLORER_MODULES: ModuleItem[] = [
  { id: 'ei-conversations', label: 'Conversations',   group: 'Communication', groupId: 'communication', sourceModule: 'edge-interact', accentColor: '#3A8C45', route: '/conversations',   icon: <ChatIcon size={18} />,       description: 'Chat interface with conversation list and message threading' },
  { id: 'ei-forms',         label: 'Forms',           group: 'Forms & Data',  groupId: 'forms',         sourceModule: 'edge-interact', accentColor: '#3A8C45', route: '/forms',           icon: <PencilIcon size={18} />,     description: 'Form submission UI with validation and context attachment' },
  { id: 'ei-activity',      label: 'Activity Stream', group: 'Monitoring',    groupId: 'monitoring',    sourceModule: 'edge-interact', accentColor: '#3A8C45', route: '/activity-stream', icon: <ActivityIcon size={18} />,   description: 'Activity log viewer with overview and timeline tabs' },
  { id: 'ei-logs',          label: 'Explorer Logs',   group: 'Monitoring',    groupId: 'monitoring',    sourceModule: 'edge-interact', accentColor: '#3A8C45', route: '/explorer-logs',   icon: <LogsIcon size={18} />,       description: 'Application state monitoring from Zustand stores' },
  { id: 'ei-settings',      label: 'Settings',        group: 'Configuration', groupId: 'configuration', sourceModule: 'edge-interact', accentColor: '#3A8C45', route: '/settings',        icon: <SettingsIcon size={18} />,   description: 'User and session context configuration' },
  // Dev Tools
  {
    id: 'ei-hil-test',
    label: 'HIL Event Tester',
    group: 'Dev Tools',
    groupId: 'dev-tools',
    sourceModule: 'edge-interact',
    accentColor: '#3b82f6',
    route: '/hil-test',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    description: 'Inject mock HIL events and test Approval / Form / Chat panels in the observe panel',
  },
];

// ─── Combined registry ────────────────────────────────────────────────────────

export const ALL_MODULES: ModuleItem[] = [...EDGE_STREAM_MODULES, ...EXPLORER_MODULES];

export function getModuleById(id: string): ModuleItem | undefined {
  return ALL_MODULES.find(m => m.id === id);
}

/**
 * Returns modules grouped by source module and nav group, preserving insertion order.
 */
export function getModuleGroups(): ModuleGroup[] {
  const groupMap = new Map<string, ModuleGroup>();
  for (const mod of ALL_MODULES) {
    const key = `${mod.sourceModule}::${mod.groupId}`;
    if (!groupMap.has(key)) {
      groupMap.set(key, {
        id: key,
        label: mod.group,
        sourceModule: mod.sourceModule,
        color: mod.accentColor,
        items: [],
      });
    }
    groupMap.get(key)!.items.push(mod);
  }
  return Array.from(groupMap.values());
}
